import type { LoggingConfig, LogLevel } from '@hunting-nature/shared-core/logging';
import { consoleSink, RingBufferSink } from '@hunting-nature/shared-core/logging';

import type { LoggingPolicy } from '../../../environments/environment.model';

/**
 * Turns the per-build `environment.logging` policy into the logger configuration.
 *
 * The policy lives in the Angular environments (one per build, fixed at build time), not in the
 * app-config files: those select a backend and can be switched at runtime, while who may read the
 * console depends on the build that was shipped. The base environment defaults to the prod policy.
 *
 * Where `allowOverride` is set (dev, fetest, stage), debug output is switched on per device from
 * the inspector console and survives a reload:
 *
 *   localStorage.setItem('secacam.logLevel', 'debug'); location.reload();
 */

export const LOG_LEVEL_OVERRIDE_KEY = 'secacam.logLevel';

const LOG_LEVELS: ReadonlySet<string> = new Set<LogLevel>(['debug', 'info', 'warn', 'error']);

/** Recent log entries of this session, for inspection and (later) export from the test section. */
export const LOG_BUFFER = new RingBufferSink(1000);

/**
 * Reads the per-device log level override that replaces the former `LOG_DEBUG` constant.
 *
 * A developer or tester sets `localStorage['secacam.logLevel']` (e.g. to `'debug'`) from the
 * inspector console, and after a reload that one device logs at the chosen level — without a code
 * change or a rebuild, and without affecting other devices. Only called when the policy has
 * `allowOverride` (dev, fetest, stage); prod never reads it.
 *
 * Returns `undefined` — so the policy's `minLevel` applies — when no value is set, the value is
 * not one of the four levels, or storage is unavailable.
 */
function readLevelOverride(storage: Pick<Storage, 'getItem'> | undefined): LogLevel | undefined {
  try {
    const value = storage?.getItem(LOG_LEVEL_OVERRIDE_KEY);
    return value && LOG_LEVELS.has(value) ? (value as LogLevel) : undefined;
  } catch {
    // Storage can be unavailable (private mode, blocked site data); fall back to the policy.
    return undefined;
  }
}

export function loggingConfigFor(policy: LoggingPolicy, storage: Pick<Storage, 'getItem'> | undefined): LoggingConfig {
  return {
    minLevel: (policy.allowOverride ? readLevelOverride(storage) : undefined) ?? policy.minLevel,
    sinks: policy.console ? [consoleSink, LOG_BUFFER] : [LOG_BUFFER],
  };
}
